import { useState } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Feather, BookOpen, Map as MapIcon, GitBranch, HeartHandshake, Languages, Quote, ShieldCheck, Lightbulb, Hand, Crown, PartyPopper, Scale, Heart, Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import { PlanGrid, IntervalSwitch } from "@/components/billing/PlanGrid";
import { BuildPlan } from "@/components/billing/BuildPlan";

const HERO_BG = "https://images.pexels.com/photos/16811482/pexels-photo-16811482.jpeg?auto=compress&cs=tinysrgb&w=1920";
const MAP_BG = "https://images.unsplash.com/photo-1520299607509-dcd935f9a839?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";
const ease = [0.22, 1, 0.36, 1];
const rise = { hidden: { opacity: 0, y: 28 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, ease, delay: i * 0.12 } }) };

const FEATURES = [
  { id: "lore", icon: BookOpen, label: "Lore Bible", title: "A living wiki for every corner of your world.", img: "lore.jpg",
    body: "Characters, places, factions, magic systems, creatures: each gets its own page with fields built for its kind. Entries link to each other automatically, and filters show what's incomplete, flagged or still a stub." },
  { id: "notes", icon: Feather, label: "Notes & AI scribe", title: "Write messy notes. Get an organized archive.", img: "entity.jpg",
    body: "Paste or dictate a raw note and the scribe sorts it into your Lore Bible. You review every change before it's saved. On each entry it points out what's missing, like a birth date or a homeland, and leaves the writing to you." },
  { id: "maps", icon: MapIcon, label: "Maps", pro: true, title: "Pin your story to the land.", img: "maps.jpg",
    body: "Upload or generate maps, drop pins linked to your entries, draw regions and borders that change over time, and play back journeys across your world." },
  { id: "timelines", icon: GitBranch, label: "Timelines & calendars", pro: true, title: "Scrub through history and see where everyone was.", img: "timelines.jpg",
    body: "Branching timelines, a time-travel scrubber, and custom calendars with your own months, moons and holidays. Characters' ages work themselves out." },
  { id: "relationships", icon: HeartHandshake, label: "Relationships & family trees", pro: true, title: "See every alliance, feud and bloodline.", img: "relationships.jpg",
    body: "A web of rivalries, marriages and friendships you can drag into shape, plus family trees that lay out each generation. Both export straight to your PDF." },
  { id: "languages", icon: Languages, label: "Languages", pro: true, title: "Give your peoples their own tongues.", img: "languages.jpg",
    body: "Dictionaries with pronunciation guides, parts of speech and example sentences, custom scripts and glyphs, and audio playback of how words sound." },
  { id: "quotes", icon: Quote, label: "Quotes, co-writing & exports", title: "Write together, then take it anywhere.", img: "quotes.jpg",
    body: "Collect memorable lines in a Quote Library, co-write live with comments and suggestions, and export a themed PDF, EPUB or Markdown Lore Bible whenever you like." },
];

const Frame = ({ src, alt, testid }) => (
  <div className="overflow-hidden rounded-lg border border-[#2D3442] bg-[#0F131A] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]" data-testid={testid}>
    <div className="flex items-center gap-1.5 border-b border-[#2D3442] px-4 py-2.5">
      {[0, 1, 2].map((i) => <span key={i} className="h-2.5 w-2.5 rounded-full bg-[#2D3442]" />)}
      <span className="ml-3 text-[10px] tracking-wide text-[#7A7468]">loreright · The Ember Reaches</span>
    </div>
    <img src={`/landing/${src}`} alt={alt} loading="lazy" className="block w-full" />
  </div>
);

const Nav = () => (
  <nav className="fixed inset-x-0 top-0 z-40 border-b border-white/5 bg-[#0B0D10]/70 backdrop-blur-xl" data-testid="landing-nav">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10">
      <a href="#top" data-testid="landing-logo"><BrandLogo testId="landing-brand" /></a>
      <div className="flex items-center gap-6 text-sm">
        <a href="#features" className="hidden text-[#A8A090] transition-colors hover:text-[#F3EBDD] sm:block" data-testid="nav-features">Features</a>
        <a href="#plans" className="hidden text-[#A8A090] transition-colors hover:text-[#F3EBDD] sm:block" data-testid="nav-plans">Plans</a>
        <Link to="/updates" className="hidden text-[#A8A090] transition-colors hover:text-[#F3EBDD] sm:block" data-testid="nav-updates">What's new</Link>
        <Link to="/login" className="text-[#A8A090] transition-colors hover:text-[#F3EBDD]" data-testid="nav-sign-in">Sign in</Link>
        <Link to="/register" className="rounded-full bg-[#F59E0B] px-4 py-1.5 font-medium text-[#0B0D10] transition-transform hover:-translate-y-0.5" data-testid="nav-start-free">Start free</Link>
      </div>
    </div>
  </nav>
);

const Hero = ({ event }) => (
  <header id="top" className="relative flex min-h-screen flex-col overflow-hidden" data-testid="landing-hero">
    <motion.img src={HERO_BG} alt="" initial={{ scale: 1.12, opacity: 0 }} animate={{ scale: 1, opacity: 0.38 }} transition={{ duration: 2.4, ease }}
      className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0B0D10]/40 via-[#0B0D10]/70 to-[#0B0D10]" />
    <div className="pointer-events-none absolute left-1/2 top-1/3 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-[#F59E0B]/10 blur-[120px]" />
    <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-6 pb-24 pt-32 sm:px-10">
      {event && (
        <motion.div variants={rise} initial="hidden" animate="show" className="mb-8 inline-flex w-fit items-center gap-2 rounded-full border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-4 py-1.5 text-sm text-[#FBBF24]" data-testid="landing-event-banner">
          <PartyPopper className="h-4 w-4" /><span><b>{event.name}:</b> Pro is free for everyone until {new Date(event.end).toLocaleDateString(undefined, { month: "long", day: "numeric" })}</span>
        </motion.div>
      )}
      <motion.div variants={rise} initial="hidden" animate="show" className="text-xs uppercase tracking-[0.3em] text-[#F59E0B]">Worldbuilding for fiction writers</motion.div>
      <motion.h1 variants={rise} initial="hidden" animate="show" custom={1} className="mt-6 max-w-4xl font-display text-5xl leading-[1.02] tracking-tight text-[#F3EBDD] sm:text-7xl lg:text-8xl" data-testid="landing-title">
        Every world deserves <span className="italic text-[#FBBF24]">a keeper.</span>
      </motion.h1>
      <motion.p variants={rise} initial="hidden" animate="show" custom={2} className="mt-8 max-w-xl text-lg leading-relaxed text-[#A8A090] sm:text-xl">
        LoreRight keeps everything behind your story in one place: characters, places, maps, timelines, languages and family trees, all connected. You build the world and it stays yours.
      </motion.p>
      <motion.div variants={rise} initial="hidden" animate="show" custom={3} className="mt-10 flex flex-wrap items-center gap-6">
        <Link to="/register" className="rounded-full bg-[#F59E0B] px-8 py-3.5 text-base font-medium text-[#0B0D10] shadow-[0_0_40px_-8px_#F59E0B] transition-transform hover:-translate-y-0.5" data-testid="hero-start-free">Start writing free</Link>
        <Link to="/login" className="text-base text-[#F3EBDD] underline-offset-4 transition-colors hover:text-[#FBBF24] hover:underline" data-testid="hero-sign-in">Sign in</Link>
      </motion.div>
    </div>
    <a href="#promise" className="relative z-10 mx-auto mb-8 flex flex-col items-center gap-1 text-xs uppercase tracking-[0.25em] text-[#A8A090] hover:text-[#F3EBDD]" data-testid="hero-scroll">
      Explore<motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}><ChevronDown className="h-5 w-5" /></motion.span>
    </a>
  </header>
);

const ScribePromise = () => (
  <section id="promise" className="relative mx-auto max-w-7xl px-6 py-28 sm:px-10" data-testid="landing-promise">
    <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} className="max-w-3xl">
      <div className="text-xs uppercase tracking-[0.3em] text-[#F59E0B]">Your story, your words</div>
      <h2 className="mt-5 font-display text-4xl leading-tight text-[#F3EBDD] sm:text-5xl">An AI scribe that suggests and never writes.</h2>
      <p className="mt-6 text-lg leading-relaxed text-[#A8A090]">The scribe files your own notes into your world and suggests what you could add next. It never writes your story, and nothing it suggests is saved until you accept it.</p>
    </motion.div>
    <div className="mt-14 grid gap-5 md:grid-cols-3">
      {[[Lightbulb, "Suggestions, not prose", "It points out gaps: a character with no homeland, a war with no date, two notes that disagree."],
        [Hand, "You approve everything", "Every extracted entry, field and link is shown for review first. Keep it, change it or throw it away."],
        [ShieldCheck, "Your world, your rules", "You decide what's canon, what's hidden from readers and what goes into your exports."]].map(([Icon, t, d], i) => (
        <motion.div key={t} variants={rise} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
          className="rounded-md border border-[#2D3442] bg-[#0F131A]/80 p-7 backdrop-blur" data-testid={`promise-card-${i}`}>
          <Icon className="h-6 w-6 text-[#FBBF24]" />
          <h3 className="mt-5 font-display text-2xl text-[#F3EBDD]">{t}</h3>
          <p className="mt-2 text-sm leading-relaxed text-[#A8A090]">{d}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

const Feature = ({ f, i }) => {
  const Icon = f.icon, flip = i % 2 === 1;
  return (
    <div className={`grid items-center gap-10 lg:grid-cols-[0.9fr_1.4fr] ${flip ? "lg:[&>*:first-child]:order-2 lg:grid-cols-[1.4fr_0.9fr]" : ""}`} data-testid={`feature-${f.id}`}>
      <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }}>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#F59E0B]">
          <Icon className="h-4 w-4" />{f.label}
          {f.pro && <span className="ml-1 inline-flex items-center gap-1 rounded-full border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-2 py-0.5 text-[10px] text-[#FBBF24]"><Crown className="h-3 w-3" />Pro</span>}
        </div>
        <h3 className="mt-5 font-display text-3xl leading-tight text-[#F3EBDD] sm:text-4xl">{f.title}</h3>
        <p className="mt-4 text-base leading-relaxed text-[#A8A090]">{f.body}</p>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 40, rotate: flip ? -1 : 1 }} whileInView={{ opacity: 1, y: 0, rotate: 0 }} viewport={{ once: true, amount: 0.25 }} transition={{ duration: 0.9, ease }}>
        <Frame src={f.img} alt={`LoreRight ${f.label}`} testid={`feature-shot-${f.id}`} />
      </motion.div>
    </div>
  );
};

const Features = () => (
  <section id="features" className="relative border-t border-white/5 py-28" data-testid="landing-features">
    <div className="mx-auto max-w-7xl px-6 sm:px-10">
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.3em] text-[#F59E0B]">Inside LoreRight</div>
        <h2 className="mt-5 font-display text-4xl leading-tight text-[#F3EBDD] sm:text-5xl">Everything your world needs, all connected.</h2>
      </div>
      <div className="mt-20 space-y-28">{FEATURES.map((f, i) => <Feature key={f.id} f={f} i={i} />)}</div>
    </div>
  </section>
);

const Ownership = () => (
  <section className="relative overflow-hidden border-t border-white/5 py-28" data-testid="landing-ownership">
    <img src={MAP_BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.07]" />
    <div className="relative mx-auto max-w-7xl px-6 sm:px-10">
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.3em] text-[#F59E0B]">LoreRight License</div>
        <h2 className="mt-5 font-display text-4xl leading-tight text-[#F3EBDD] sm:text-5xl">You own everything you create. Full stop.</h2>
      </div>
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {[[Scale, "100% yours", "Your worlds, characters, maps and words belong to you. LoreRight claims no ownership, ever."],
          [Heart, "Hobby use never costs extra", "Writing for fun or running a game for friends? There's no extra charge for personal projects."],
          [Briefcase, "Selling your work?", "If you publish or sell what you build, we kindly ask you to switch to the Commercial plan. It's on the honor system, with no royalties."]].map(([Icon, t, d], i) => (
          <motion.div key={t} variants={rise} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="rounded-md border border-[#2D3442] bg-[#0B0D10]/80 p-7" data-testid={`ownership-card-${i}`}>
            <Icon className="h-6 w-6 text-[#FBBF24]" />
            <h3 className="mt-5 font-display text-2xl text-[#F3EBDD]">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#A8A090]">{d}</p>
          </motion.div>
        ))}
      </div>
      <Link to="/license" className="mt-8 inline-block text-sm text-[#F59E0B] hover:underline" data-testid="landing-license-link">Read the full LoreRight License &amp; Terms →</Link>
    </div>
  </section>
);

const Plans = ({ data }) => {
  const [interval, setPeriod] = useState("year");
  const cta = (t) => (
    <Link to="/register" data-testid={`plan-${t}-cta`}
      className={`block rounded-full px-5 py-2.5 text-center text-sm font-medium transition-transform hover:-translate-y-0.5 ${t === "pro" ? "bg-[#F59E0B] text-[#0B0D10]" : "border border-[#2D3442] text-[#F3EBDD] hover:border-[#F59E0B]/60"}`}>
      {t === "free" ? "Start writing free" : t === "pro" ? "Start with a free 7-day Pro trial" : "Try it free, then go Commercial"}
    </Link>
  );
  return (
    <section id="plans" className="border-t border-white/5 py-28" data-testid="landing-plans">
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[#F59E0B]">Plans</div>
            <h2 className="mt-5 font-display text-4xl leading-tight text-[#F3EBDD] sm:text-5xl">Free to start. Pro when your world grows.</h2>
            <p className="mt-4 text-base text-[#A8A090]">Every new account gets a free 7-day Pro trial, no card needed.</p>
          </div>
          <IntervalSwitch value={interval} onChange={setPeriod} prices={data?.prices} testid="landing-interval-switch" />
        </div>
        {data?.event && (
          <div className="mt-8 flex items-center gap-3 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-5 py-3 text-sm text-[#FBBF24]" data-testid="plans-event-banner">
            <PartyPopper className="h-4 w-4 shrink-0" /><span><b>Event: {data.event.name}.</b> Pro is free for everyone until {new Date(data.event.end).toLocaleDateString(undefined, { month: "long", day: "numeric" })}.</span>
          </div>
        )}
        <div className="mt-12"><PlanGrid prices={data?.prices} interval={interval} action={cta} /></div>
        <BuildPlan prices={data?.prices} interval={interval} action={({ disabled }) => (
          <Link to="/register" className={`rounded-full px-5 py-2.5 text-sm font-medium ${disabled ? "pointer-events-none border border-[#2D3442] text-[#7A7468]" : "bg-[#F59E0B] text-[#0B0D10]"}`} data-testid="landing-build-cta">Start free, pick later</Link>
        )} />
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="border-t border-white/5" data-testid="landing-footer">
    <div className="mx-auto max-w-7xl px-6 py-24 text-center sm:px-10">
      <h2 className="font-display text-4xl text-[#F3EBDD] sm:text-6xl">Your world is waiting.</h2>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
        <Link to="/register" className="rounded-full bg-[#F59E0B] px-8 py-3.5 font-medium text-[#0B0D10] shadow-[0_0_40px_-8px_#F59E0B] transition-transform hover:-translate-y-0.5" data-testid="footer-start-free">Start writing free</Link>
        <Link to="/login" className="text-[#F3EBDD] hover:text-[#FBBF24]" data-testid="footer-sign-in">Sign in</Link>
      </div>
    </div>
    <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 border-t border-white/5 px-6 py-6 text-xs text-[#7A7468] sm:px-10">
      <span>© {new Date().getFullYear()} LoreRight</span>
      <div className="flex gap-6">
        <Link to="/license" className="hover:text-[#F3EBDD]" data-testid="footer-license-link">License &amp; Terms</Link>
        <a href="#plans" className="hover:text-[#F3EBDD]">Plans</a>
        <Link to="/updates" className="hover:text-[#F3EBDD]" data-testid="footer-updates">What's new</Link>
        <Link to="/login" className="hover:text-[#F3EBDD]">Sign in</Link>
      </div>
    </div>
  </footer>
);

export default function LandingPage() {
  const { data } = useQuery({ queryKey: ["public-plans"], queryFn: () => api.get("/public/plans").then((r) => r.data) });
  return (
    <div className="min-h-screen scroll-smooth bg-[#0B0D10] lf-grain" data-testid="landing-page">
      <Nav />
      <Hero event={data?.event} />
      <ScribePromise />
      <Features />
      <Ownership />
      <Plans data={data} />
      <Footer />
    </div>
  );
}
